import { CommonModule } from '@angular/common';
import { Component, Input, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CaseFormService } from 'app/case-form/case-form.service';
import { Subject, take, takeUntil } from 'rxjs';
import { COMMENT_TYPES, ICaseComment } from 'app/shared/models/comment.model';
import { CheckboxFormFieldComponent } from '../forms/fields/checkbox-form-field/checkbox-form-field.component';
import { RichTextEditorComponent } from '../forms/fields/rich-text-editor/rich-text-editor.component';
import { HelperService } from 'app/shared/services/helper.service';
import { CaseTypeEnum } from 'app/shared/enums/case-type.enum';

@Component({
  selector: 'app-case-comments',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    CheckboxFormFieldComponent,
    RichTextEditorComponent
  ],
  templateUrl: './case-comments.component.html',
  styleUrl: './case-comments.component.scss',
})
export class CaseCommentsComponent {
  @Input() caseId!: number;
  @Input() disabled!: boolean;
  @Input() commentsFilterForm!: FormGroup | any;
  @Input() caseType!: string;
  comments: ICaseComment[] = [];
  activeItem: ICaseComment | any = {};
  commentForm!: FormGroup | any;
  replyTo!: string;
  activeId!: number | null;
  COMMENT_TYPES = COMMENT_TYPES;
  private destroy$ = new Subject<void>();

  constructor(private caseFormService: CaseFormService, public helperService: HelperService) { }

  ngOnChanges(changes: SimpleChanges): void {
    const isProblemRecord = this.caseType === CaseTypeEnum.PROBLEM_RECORD;

    if (isProblemRecord && (changes['caseId'] || changes['caseType']) && this.caseId) {
     this.getComments();
    }
    
    if (changes['commentsFilterForm'] && changes['commentsFilterForm'].firstChange) {
      this.getComments();

      this.commentsFilterForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.getComments();
      });
    }
  }

  ngOnInit() {
    this.createForm();
  }

  getComments(item?: any) {
    if (this.caseId) {
      const type =
      this.caseType === CaseTypeEnum.CASE && this.commentsFilterForm?.value?.showChatsOnly
        ? COMMENT_TYPES.ChatConversation
        : undefined;
      this.caseFormService.getCaseComments(this.caseType ,this.caseId, item?.id, type)
        .pipe((take(1)))
        .subscribe((res: any) => {
          const updatedComments = res.map((c: any) => ({
          ...c,
          caseCommentType: c.caseCommentType ?? COMMENT_TYPES.Comment
        }));

        if (item && item.id) {
          item.replies = updatedComments;
          this.comments = [...this.comments];
        } else {
          this.comments = updatedComments;
        }
        });
    }
  }

  getCommentsOnSave(newCommentId: number, activeId?: number | null) {
    if (this.caseId) {
      const type =
      this.caseType === CaseTypeEnum.CASE && this.commentsFilterForm?.value?.showChatsOnly
        ? COMMENT_TYPES.ChatConversation
        : undefined;
      this.caseFormService.getCaseComments(this.caseType ,this.caseId, activeId, type)
        .pipe((take(1)))
        .subscribe((res: any) => {
          const updatedComments = res.map((c: any) => ({
          ...c,
          caseCommentType: c.caseCommentType ?? COMMENT_TYPES.Comment
        }));
        this.setNewComment(updatedComments, newCommentId, !activeId);
        });
    }
  }

  setNewComment(res: any, newCommentId: number, rootComment?: boolean) {
    res.forEach((item: any) => {
      if (item.id === newCommentId) {
        if (!rootComment) {
          this.activeItem.replies = this.activeItem.replies ?? [];
          this.activeItem.replies.push(item);
        } else {
          this.comments.unshift(item);
        }
      }
    });
  }

  loadReply(item: any) {
    item.hideLoadBtn = true;
    this.getComments(item);
  }

  createForm() {
    this.commentForm = new FormGroup({
      caseId: new FormControl(this.caseId, [Validators.required]),
      parentId: new FormControl(null, []),
      content: new FormControl(null, [Validators.required])
    });
  }

  resetForm() {
    this.commentForm.get('parentId').reset();
    this.commentForm.get('content').reset();
    this.activeId = null;
    this.replyTo = '';
  }

  replyComment(item: ICaseComment) {
    this.activeItem = item;
    this.replyTo = item.createdBy;
    this.activeId = item.id;
    this.commentForm.get('parentId').setValue(item.id);
  }

  submitForm() {
    const caseCommentData = this.commentForm.getRawValue();
    this.caseFormService.saveCaseComments(this.caseType, caseCommentData)
      .pipe((take(1)))
      .subscribe((res) => {
        this.getCommentsOnSave(res, this.activeId);
        this.resetForm();

      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
